import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useRouter } from 'vue-router'

export interface SessionTimeoutConfig {
  timeoutDuration: number // in milliseconds
  warningDuration: number // in milliseconds
  checkInterval: number // in milliseconds
  activityEvents: string[]
  enableLogging?: boolean // optional logging flag
}

export interface SessionTimeoutState {
  isActive: boolean
  lastActivity: number
  showWarning: boolean
  timeRemaining: number
  isTabVisible: boolean
}

export function useSessionTimeout(config?: Partial<SessionTimeoutConfig>) {
  const authStore = useAuthStore()
  const router = useRouter()

  // Configuration with defaults
  const defaultConfig: SessionTimeoutConfig = {
    timeoutDuration: parseInt(import.meta.env.VITE_SESSION_TIMEOUT_MINUTES || '15') * 60 * 1000, // 15 minutes
    warningDuration: parseInt(import.meta.env.VITE_SESSION_WARNING_MINUTES || '5') * 60 * 1000, // 5 minutes
    checkInterval: 1000, // 1 second
    activityEvents: [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
      'focus',
      'blur',
    ],
    enableLogging: import.meta.env.VITE_SESSION_LOGGING === 'true', // Enable logging based on env var
  }

  const sessionConfig = { ...defaultConfig, ...config }

  // Reactive state
  const state = ref<SessionTimeoutState>({
    isActive: false,
    lastActivity: Date.now(),
    showWarning: false,
    timeRemaining: sessionConfig.timeoutDuration,
    isTabVisible: true,
  })

  // Timers
  let timeoutTimer: ReturnType<typeof setTimeout> | null = null
  let warningTimer: ReturnType<typeof setTimeout> | null = null
  let checkTimer: ReturnType<typeof setInterval> | null = null

  // Computed properties
  const isWarningActive = computed(() => state.value.showWarning)
  const timeRemainingMinutes = computed(() => Math.ceil(state.value.timeRemaining / 60000))
  const timeRemainingSeconds = computed(() => Math.ceil((state.value.timeRemaining % 60000) / 1000))

  // Activity tracking
  const updateLastActivity = () => {
    if (!state.value.isActive || !authStore.isAuthenticated) return

    const now = Date.now()
    state.value.lastActivity = now
    state.value.timeRemaining = sessionConfig.timeoutDuration

    // Hide warning if showing
    if (state.value.showWarning) {
      state.value.showWarning = false
    }

    // Reset timers
    resetTimers()
    startTimers()
  }

  // API request activity tracking
  const trackApiActivity = () => {
    updateLastActivity()
  }

  // Token refresh activity tracking
  const trackTokenRefresh = () => {
    // Token refresh should extend the session
    updateLastActivity()

    if (sessionConfig.enableLogging) {
      console.log('[SessionTimeout] Token refreshed, session extended')
    }
  }

  // Tab visibility handling
  const handleVisibilityChange = () => {
    state.value.isTabVisible = !document.hidden

    if (state.value.isTabVisible && state.value.isActive) {
      // Resume timers when tab becomes visible
      startTimers()
    } else {
      // Pause timers when tab is hidden
      pauseTimers()
    }
  }

  // Timer management
  const resetTimers = () => {
    if (timeoutTimer) {
      clearTimeout(timeoutTimer)
      timeoutTimer = null
    }
    if (warningTimer) {
      clearTimeout(warningTimer)
      warningTimer = null
    }
  }

  const pauseTimers = () => {
    resetTimers()
  }

  const startTimers = () => {
    if (!state.value.isActive || !authStore.isAuthenticated || !state.value.isTabVisible) return

    resetTimers()

    // Set warning timer
    const warningTime = sessionConfig.timeoutDuration - sessionConfig.warningDuration
    warningTimer = setTimeout(() => {
      if (state.value.isActive && authStore.isAuthenticated) {
        state.value.showWarning = true
      }
    }, warningTime)

    // Set timeout timer
    timeoutTimer = setTimeout(() => {
      if (state.value.isActive && authStore.isAuthenticated) {
        handleSessionTimeout()
      }
    }, sessionConfig.timeoutDuration)
  }

  // Session timeout handler
  const handleSessionTimeout = async () => {
    state.value.showWarning = false

    try {
      // Log out user and clear all data
      await authStore.logout()

      // Navigate to login with timeout message
      router.push({
        path: '/login',
        query: {
          reason: 'session_timeout',
          message: 'Your session has expired due to inactivity. Please log in again.',
        },
      })
    } catch (error) {
      console.error('Error during session timeout logout:', error)
      // Force navigation even if logout fails
      router.push({
        path: '/login',
        query: {
          reason: 'session_timeout',
          message: 'Your session has expired due to inactivity. Please log in again.',
        },
      })
    } finally {
      stopSessionTimeout()
    }
  }

  // Extend session
  const extendSession = () => {
    updateLastActivity()
    state.value.showWarning = false
  }

  // Update time remaining (called by interval timer)
  const updateTimeRemaining = () => {
    if (!state.value.isActive || !authStore.isAuthenticated || !state.value.isTabVisible) return

    const now = Date.now()
    const elapsed = now - state.value.lastActivity
    const remaining = Math.max(0, sessionConfig.timeoutDuration - elapsed)

    state.value.timeRemaining = remaining

    if (remaining <= 0) {
      handleSessionTimeout()
    }
  }

  // Start session timeout monitoring
  const startSessionTimeout = () => {
    if (state.value.isActive || !authStore.isAuthenticated) return

    state.value.isActive = true
    state.value.lastActivity = Date.now()
    state.value.timeRemaining = sessionConfig.timeoutDuration
    state.value.showWarning = false

    // Add activity event listeners
    sessionConfig.activityEvents.forEach((event) => {
      document.addEventListener(event, updateLastActivity, { passive: true })
    })

    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Expose API activity tracking globally for axios interceptor
    if (typeof window !== 'undefined') {
      ;(window as any).__sessionTimeout = {
        trackApiActivity,
        trackTokenRefresh,
      }
    }

    // Start timers
    startTimers()

    // Start interval timer for updating time remaining
    checkTimer = setInterval(updateTimeRemaining, sessionConfig.checkInterval)
  }

  // Stop session timeout monitoring
  const stopSessionTimeout = () => {
    if (!state.value.isActive) return

    state.value.isActive = false
    state.value.showWarning = false

    // Remove event listeners
    sessionConfig.activityEvents.forEach((event) => {
      document.removeEventListener(event, updateLastActivity)
    })

    document.removeEventListener('visibilitychange', handleVisibilityChange)

    // Clean up global reference
    if (typeof window !== 'undefined') {
      delete (window as any).__sessionTimeout
    }

    // Clear all timers
    resetTimers()
    if (checkTimer) {
      clearInterval(checkTimer)
      checkTimer = null
    }
  }

  // Lifecycle hooks
  onMounted(() => {
    if (authStore.isAuthenticated) {
      startSessionTimeout()
    }
  })

  onUnmounted(() => {
    stopSessionTimeout()
  })

  return {
    // State
    state: computed(() => state.value),
    isWarningActive,
    timeRemainingMinutes,
    timeRemainingSeconds,

    // Methods
    startSessionTimeout,
    stopSessionTimeout,
    extendSession,
    trackApiActivity,
    trackTokenRefresh,
    updateLastActivity,

    // Config
    config: sessionConfig,
  }
}
